import { Account } from '@ally/data-types-afg-accounts'
import {
  Widget,
  CustomizationOptions,
  Lob,
  AccountType,
} from '@ally/data-types-customization'
import { LoadState } from '@ally/use-data-domain'
import { QueryStatus } from '@tanstack/react-query'
import * as Ramda from 'ramda'
import {
  AccountTypesObj,
  CustomizeDataObj,
  DashboardView,
  OrderObj,
  ViewsObj,
  WidgetConfigObj,
  WidgetKey,
} from './types'
import {
  groupBankAccounts,
  groupInvestAccounts,
  hasAcceptedBankruptcy,
} from '../utils'

export function getDisplayName(lobName: string | undefined): string {
  if (lobName === 'CC') {
    return 'Credit Card'
  }
  return lobName || ''
}

/** idx + 1 ensures that the order starts at 1 */
export function reorder<T extends { order: number }>(
  someObj: T,
  idx: number,
): T {
  return { ...someObj, order: idx + 1 }
}

export function reorderWidgets(widgets: Widget[]): Widget[] {
  const isDefaultOn = widgets.some(w => w.enabled !== undefined)
  const enabledWidgets = widgets
    .filter(widget => (isDefaultOn ? widget.enabled : true))
    .map(reorder)

  const disabledWidgets = isDefaultOn
    ? widgets
        .filter(w => !w.enabled)
        .map((widget, idx) => {
          // make the order negative just for the visual difference
          return { ...widget, order: -1 - idx }
        })
    : []

  return enabledWidgets.concat(disabledWidgets)
}

export function getDisabledWidgets(widgets: Widget[]): Widget[] {
  return widgets.filter(widget => widget.enabled === false)
}

export function getFormattedData({
  activeDepositAccounts,
  investAccounts,
  isBankOutage,
  isInvestOutage,
  isWealthFullOutage,
  isWealthProspect,
  isMortgageCustomer,
  isAutoCustomer,
  isDebtPlanUser,
  isCCUser,
  creditCardAccounts,
  autoAccounts,
}: {
  activeDepositAccounts: Account[]
  investAccounts: Account[]
  isBankOutage: boolean
  isInvestOutage: boolean
  isWealthFullOutage: boolean
  isWealthProspect: boolean
  isMortgageCustomer: boolean
  isAutoCustomer: boolean
  isDebtPlanUser: boolean
  isCCUser: boolean
  creditCardAccounts: Account[]
  autoAccounts: Account[]
}): CustomizeDataObj {
  const getLobs = (): OrderObj[] => {
    const isInvestLobAvailable =
      investAccounts.length >= 1 ||
      isInvestOutage ||
      isWealthFullOutage ||
      isWealthProspect

    const isDepositLobAvailable =
      activeDepositAccounts.length >= 1 || isBankOutage

    const investLob = isInvestLobAvailable ? [{ name: 'INVEST', order: 1 }] : []
    const depositLob = isDepositLobAvailable
      ? [{ name: 'DEPOSIT', order: 2 }]
      : []
    const ccLob = isCCUser ? [{ name: 'CC', order: 3 }] : []
    const mortgageLob = isMortgageCustomer
      ? [{ name: 'MORTGAGE', order: 4 }]
      : []

    // bankrupt auto account is removed from autoAccounts[]
    // isBankruptcyAccepted check is required to show No Active Vehicle card if all accounts are in bankruptcy state
    const isBankruptcyAccepted = hasAcceptedBankruptcy()
    const isAutoLobAvailable =
      isAutoCustomer && (autoAccounts.length >= 1 || !isBankruptcyAccepted)
    const autoLob = isAutoLobAvailable ? [{ name: 'AUTO', order: 5 }] : []

    // not technically an LOB
    const debtPlan = isDebtPlanUser ? [{ name: 'DEBT_PLAN', order: 6 }] : []

    /* 
      ORDER MATTERS IN THIS ARRAY!!! This represents the default order of LOB's.

      Example: if a user is a mortgage only customer invest and deposit will be empty arrays leaving
      just the mortgageLob and the `reorder` function will properly assign 1 
    */
    return [investLob, depositLob, ccLob, mortgageLob, autoLob, debtPlan]
      .flat()
      .map(reorder)
  }

  const ccAccountTypes: string[] = creditCardAccounts.map(acc => acc.type)
  const defaultCreditCardAccountTypes: AccountTypesObj[] =
    creditCardAccounts.length > 0
      ? [
          { name: 'PLATINUM', lob: 'CC', order: 1 },
          { name: 'EVERYDAY', lob: 'CC', order: 2 },
          { name: 'UNLIMITED', lob: 'CC', order: 3 },
        ]
          .filter(accountType => ccAccountTypes.includes(accountType.name))
          .map(reorder)
      : []

  const accountTypes: AccountTypesObj[] = [
    ...Object.keys(groupInvestAccounts(investAccounts)).map((type, index) => {
      return {
        name: type,
        lob: 'INVEST',
        order: index + 1,
      }
    }),
    ...Object.keys(groupBankAccounts(activeDepositAccounts)).map(
      (type, index) => {
        return {
          name: type,
          lob: 'DEPOSIT',
          order: index + 1,
        }
      },
    ),
    ...defaultCreditCardAccountTypes,
  ]
  const views = [
    {
      name: 'DASHBOARD',
      value: DashboardView.DEFAULT,
    },
  ] as ViewsObj[]
  const widgets = [] as Widget[]

  return {
    channel: 'WEB',
    views,
    lobs: getLobs(),
    accountTypes,
    widgets,
  }
}

const sortAccountType = (
  accountTypes: AccountType[],
  lob: string,
): AccountType[] => {
  return accountTypes
    .filter(account => account.lob === lob)
    .sort((a, b) => a.order - b.order)
}

export function getUpdatedCustomizeDataObj({
  defaultCustomizations,
  customizationResponse,
  eligibleWidgets,
  isDebtPlanUser,
}: {
  defaultCustomizations: CustomizeDataObj
  customizationResponse: CustomizationOptions
  eligibleWidgets: WidgetConfigObj
  isDebtPlanUser: boolean
}): CustomizationOptions {
  const compare = (
    x: OrderObj | AccountTypesObj,
    y: OrderObj | AccountTypesObj,
  ): boolean => x.name === y.name

  const newLobs: Lob[] = Ramda.differenceWith(
    compare,
    [...defaultCustomizations.lobs] as Lob[],
    [...customizationResponse.lobs],
  )
  const existingLobs = customizationResponse.lobs

  const sortedExistingLobs = [...existingLobs].sort((a, b) => a.order - b.order)

  // This will give newLobs priority index and the existing LOB's will follow
  const mergedSortedLobs = newLobs
    .concat(sortedExistingLobs)
    .filter(lob => {
      const defaultLobNames = defaultCustomizations.lobs.map(({ name }) => name)
      // This filter removes customized LOBs that were received from the API that the user no longer has
      return defaultLobNames.includes(lob.name)
    })
    .map(reorder)

  const filteredLobs = mergedSortedLobs.filter(lob => {
    return !(lob.name === 'DEBT_PLAN' && !isDebtPlanUser)
  })

  const newAccountTypes = Ramda.differenceWith(
    compare,
    [...defaultCustomizations.accountTypes] as AccountType[],
    [...customizationResponse.accountTypes],
  )

  const lobsWithAccountTypes = ['INVEST', 'DEPOSIT', 'CC']

  const mergedSortedAccountTypes = lobsWithAccountTypes
    .map(lob => {
      const newAccTypes = sortAccountType(newAccountTypes, lob)
      const existingAccTypes = sortAccountType(
        customizationResponse.accountTypes,
        lob,
      )
      return newAccTypes
        .concat(existingAccTypes)
        .filter(accountType => {
          const defaultAccountTypeNames = defaultCustomizations.accountTypes.map(
            ({ name }) => name,
          )
          // This filter removes customized Account Types that were received from the API that the user no longer has
          return defaultAccountTypeNames.includes(accountType.name)
        })
        .map(reorder)
    })
    .flat()

  // Exclude widgets that aren't eligible for display
  const widgets = customizationResponse.widgets
    .filter(widget => Object.keys(eligibleWidgets).includes(widget.name))
    .sort((a, b) => a.order - b.order)

  // Force the DEFAULT view if there are no eligible widgets to display
  const views = customizationResponse.views.map(view => {
    if (view.name === 'DASHBOARD') {
      return widgets.length > 0 ? view : { ...view, value: 'DEFAULT' }
    }
    return view
  })

  return {
    channel: 'WEB',
    views,
    lobs: filteredLobs,
    accountTypes: mergedSortedAccountTypes,
    widgets,
    customizationId: customizationResponse.customizationId,
    customerId: customizationResponse.customerId,
  }
}

export function getEligibleWidgets(widgets: WidgetConfigObj): WidgetConfigObj {
  return Object.keys(widgets).reduce((accum, widgetId) => {
    const key = widgetId as WidgetKey
    if (widgets[key]?.isEligible()) {
      return {
        ...accum,
        [widgetId]: widgets[key],
      }
    }
    return { ...accum }
  }, {} as WidgetConfigObj)
}

export function normalizeQueryStatus(status: QueryStatus): LoadState {
  const statusMap = {
    success: LoadState.LOADED,
    error: LoadState.FAILED,
    loading: LoadState.LOADING,
  }[status]

  return statusMap
}
